// usage-cdn-view.tsx — CDN + WAAP bandwidth analytics for the usage tab.
// Both categories use the CDN analytics backbone (SwiftFederation.CDN store).
import { useMemo } from 'react';
import { Button, Card, CardBody, CardTitle, Skeleton } from '@vnetwork/ui/primitives';
import { EmptyState, Stat, StatGroup } from '@vnetwork/ui/data';
import { Alert } from '@vnetwork/ui/feedback';
import { Chart, CHART_COLORS } from '@vnetwork/ui/charts';
import { useOpsCDNAnalytics, useOpsCDNSummary } from '@/api/operations';

/* ── Chart palette ────────────────────────────────────────────────────────── */

const COLOR_BANDWIDTH = CHART_COLORS[0]; // blue

/* ── Formatters ───────────────────────────────────────────────────────────── */

function formatBytes(bytes: number): string {
  if (!isFinite(bytes) || bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
  const i = Math.floor(Math.log(Math.abs(bytes)) / Math.log(1024));
  return `${(bytes / Math.pow(1024, Math.min(i, units.length - 1))).toFixed(2)} ${units[Math.min(i, units.length - 1)]}`;
}

function formatBps(bps: number): string {
  if (!isFinite(bps) || bps === 0) return '0 bps';
  const units = ['bps', 'Kbps', 'Mbps', 'Gbps', 'Tbps'];
  const i = Math.floor(Math.log(Math.abs(bps)) / Math.log(1000));
  return `${(bps / Math.pow(1000, Math.min(i, units.length - 1))).toFixed(2)} ${units[Math.min(i, units.length - 1)]}`;
}

/* ── Props ────────────────────────────────────────────────────────────────── */

interface CdnUsageViewProps {
  serviceId: string;
  /** YYYY-MM-DD local date strings — converted to RFC3339 internally. */
  fromDate: string;
  toDate: string;
}

/* ── Component ───────────────────────────────────────────────────────────── */

/**
 * CDN + WAAP usage view — bandwidth analytics via the CDN analytics backbone.
 * Accepts local YYYY-MM-DD date strings; converts to RFC3339 for the hooks.
 */
export function CdnUsageView({ serviceId, fromDate, toDate }: CdnUsageViewProps) {
  const from = useMemo(() => new Date(fromDate).toISOString(), [fromDate]);
  const to = useMemo(() => new Date(toDate).toISOString(), [toDate]);

  const analytics = useOpsCDNAnalytics(serviceId, { from, to, metric: 'bandwidth' });
  const summary = useOpsCDNSummary(serviceId, { from, to });

  const chartData = useMemo(
    () => (analytics.data?.series ?? []).map((p) => ({ t: p.timestamp, bandwidth: p.value })),
    [analytics.data],
  );

  const isLoading = analytics.isLoading || summary.isLoading;
  const isError = analytics.isError || summary.isError;
  const summaryData = summary.data;

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      {isError && (
        <Alert
          tone="danger"
          title="Failed to load usage data"
          actions={
            <Button
              size="sm"
              variant="ghost"
              onClick={() => { analytics.refetch(); summary.refetch(); }}
            >
              Retry
            </Button>
          }
        />
      )}

      {!isError && (
        <StatGroup columns={4}>
          {isLoading ? (
            <>{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} style={{ height: 72 }} />)}</>
          ) : (
            <>
              <Stat label="Total Volume" value={summaryData ? formatBytes(summaryData.volume_bytes) : '—'} hint={`${fromDate} → ${toDate}`} />
              <Stat label="Peak Bandwidth" value={summaryData ? formatBps(summaryData.peak_bandwidth_bps) : '—'} />
              <Stat label="Peak RPS" value={summaryData ? String(summaryData.peak_rps.toFixed(0)) : '—'} />
              <Stat label="Domains" value={summaryData ? String(summaryData.domain_count) : '—'} />
            </>
          )}
        </StatGroup>
      )}

      {!isError && (
        <Card>
          <CardBody>
            <CardTitle style={{ marginBottom: 'var(--vn-space-3)' }}>
              Bandwidth over time
            </CardTitle>
            {isLoading ? (
              <Skeleton style={{ height: 240 }} />
            ) : chartData.length > 0 ? (
              <Chart
                type="area"
                height={240}
                data={chartData}
                xKey="t"
                series={[{ key: 'bandwidth', label: 'Bandwidth', color: COLOR_BANDWIDTH }]}
                showLegend
                showGrid
                valueFormatter={(v) => formatBps(typeof v === 'number' ? v : Number(v) || 0)}
              />
            ) : (
              <EmptyState size="sm" title="No usage data" description="No bandwidth data for the selected range." />
            )}
          </CardBody>
        </Card>
      )}
    </div>
  );
}
